import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Clock, Eye, Mail, CheckCircle, ClipboardList, Image as ImageIcon, MapPin, Calendar } from 'lucide-react';
import { ExceptionType, ExceptionStatus } from '@/types/asset-audit';
import type { AuditException } from '@/types/asset-audit';
import { ExceptionCardMenu } from './ExceptionCardMenu';

// Helper function to format exception type
function _formatExceptionType(type: ExceptionType): string {
  const typeMap: Partial<Record<ExceptionType, string>> = {
    [ExceptionType.LocationMismatch]: 'Location Mismatch',
    [ExceptionType.ConditionIssue]: 'Condition Issue',
    [ExceptionType.DataDiscrepancy]: 'Data Discrepancy',
    [ExceptionType.NotFound]: 'Not Found',
    [ExceptionType.DuplicateAsset]: 'Duplicate Asset',
    [ExceptionType.ComplianceIssue]: 'Compliance Issue',
    [ExceptionType.MaintenanceRequired]: 'Maintenance Required',
    [ExceptionType.UnauthorizedMovement]: 'Unauthorised Movement',
    [ExceptionType.AssetMissing]: 'Asset Missing',
    [ExceptionType.AssetDamaged]: 'Asset Damaged',
    [ExceptionType.ConditionMismatch]: 'Condition Mismatch',
    [ExceptionType.Other]: 'Other',
  };
  return typeMap[type] || type;
}

// Helper function to format status
function formatStatus(status: ExceptionStatus): string {
  const statusMap: Partial<Record<ExceptionStatus, string>> = {
    [ExceptionStatus.Open]: 'Open',
    [ExceptionStatus.InProgress]: 'In Progress',
    [ExceptionStatus.UnderReview]: 'Under Review',
    [ExceptionStatus.ActionRequired]: 'Action Required',
    [ExceptionStatus.Resolved]: 'Resolved',
    [ExceptionStatus.Closed]: 'Closed',
    [ExceptionStatus.Escalated]: 'Escalated',
  };
  return statusMap[status] || status;
}

interface ExceptionCardViewProps {
  exceptions: AuditException[];
  onSelectException: (exception: AuditException) => void;
  selectedExceptions?: Set<string>;
  onToggleSelection?: (exceptionId: string) => void;
  onContactAuditor?: (exception: AuditException) => void;
  onResolve?: (exception: AuditException) => void;
  onCreateWorkItem?: (exception: AuditException) => void;
  onViewAsset?: (assetId: string | number) => void;
  onViewAudit?: (auditId: string | number) => void;
  onEdit?: (exception: AuditException) => void;
  onChangePriority?: (exception: AuditException) => void;
  onDismiss?: (exception: AuditException) => void;
}

const _getSeverityColor = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getSeverityDotColor = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-500';
    case 'high':
      return 'bg-orange-500';
    case 'medium':
      return 'bg-yellow-500';
    default:
      return 'bg-blue-500';
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'bg-red-100 text-red-800';
    case 'in progress':
      return 'bg-blue-100 text-blue-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    case 'closed':
      return 'bg-gray-100 text-gray-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const _getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case 'urgent':
      return 'bg-red-100 text-red-800 border-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-800 border-orange-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    case 'low':
      return 'bg-blue-100 text-blue-800 border-blue-300';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300';
  }
};

const _getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const ExceptionCardView: React.FC<ExceptionCardViewProps> = ({
  exceptions,
  onSelectException,
  selectedExceptions = new Set(),
  onToggleSelection,
  onContactAuditor,
  onResolve,
  onCreateWorkItem,
  onViewAsset,
  onViewAudit,
  onEdit,
  onChangePriority,
  onDismiss,
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {exceptions.map((exception) => {
        const isSelected = selectedExceptions.has(exception.id);
        const isCritical = exception.severity.toLowerCase() === 'critical';
        const isUrgent = exception.priority?.toLowerCase() === 'urgent';
        const hasImage = exception.images && exception.images.length > 0;
        const daysOpen = Math.floor((new Date().getTime() - new Date(exception.reportedDate).getTime()) / (1000 * 60 * 60 * 24));
        
        return (
          <Card
            key={exception.id}
            className={`hover:shadow-lg transition-all cursor-pointer relative flex flex-col border border-border ${
              isCritical ? 'border-red-300' : ''
            } ${isUrgent ? 'border-l-4 border-l-orange-500' : ''} ${
              isSelected ? 'ring-2 ring-primary border-primary bg-primary/10 shadow-md' : ''
            }`}
            onClick={() => onSelectException(exception)}
          >
            <CardContent className="p-5 flex flex-col flex-1">
              {/* Header with Checkbox, Severity, Asset Code and Menu */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-2 flex-1">
                  {onToggleSelection && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelection(exception.id)}
                      onClick={(e) => e.stopPropagation()}
                      className={`mt-1 ${isSelected ? 'border-primary bg-primary text-primary-foreground' : ''}`}
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                                                <span className={`inline-block w-2 h-2 rounded-full ${getSeverityDotColor(exception.severity)}`} />
                        {exception.severity} - {exception.assetCode}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {exception.assetName || 'Unknown Asset'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${getStatusColor(exception.status)} pointer-events-none`}>
                    {formatStatus(exception.status)}
                  </Badge>
                  <ExceptionCardMenu
                    exception={exception}
                    onViewDetails={() => onSelectException(exception)}
                    onEdit={() => onEdit?.(exception)}
                    onAssign={() => onCreateWorkItem?.(exception)}
                    onChangePriority={() => onChangePriority?.(exception)}
                    onContactAuditor={() => onContactAuditor?.(exception)}
                    onResolve={() => onResolve?.(exception)}
                    onDismiss={() => onDismiss?.(exception)}
                    onViewAsset={() => onViewAsset?.(exception.assetId)}
                    onViewAudit={() => onViewAudit?.(exception.auditId)}
                  />
                </div>
              </div>

              {/* Exception Image - Always present */}
              <div className="mb-3">
                <div className="w-full h-48 bg-gray-100 rounded-lg border overflow-hidden">
                  {hasImage ? (
                    <img
                      src={exception.images![0].url}
                      alt="Exception"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="h-16 w-16" />
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-3 flex-1">
                {exception.description && (
                  <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                    {exception.description}
                  </p>
                )}
              </div>

              {/* Metadata Row */}
              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap mb-3">
                {exception.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{exception.location}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  <span>{new Date(exception.reportedDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 flex-shrink-0" />
                  <span>{daysOpen} day{daysOpen !== 1 ? 's' : ''} open</span>
                </div>
              </div>

              {/* Divider above action buttons */}
              <div className="border-t pt-3 mt-auto">
                {/* Action Buttons */}
                <div className="grid grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectException(exception);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    <span className="text-xs">Review</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactAuditor?.(exception);
                    }}
                  >
                    <Mail className="h-4 w-4" />
                    <span className="text-xs">Contact</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onResolve?.(exception);
                    }}
                  >
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-xs">Resolve</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-1 h-auto py-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateWorkItem?.(exception);
                    }}
                  >
                    <ClipboardList className="h-4 w-4" />
                    <span className="text-xs">Work Item</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
